// Alert Context Banner Component
// Displays alert context information as a standalone banner
// Used between Portal View Summary and Alerts list

import {
  Error as ErrorIcon,
  Info as InfoIcon,
  TrendingDown as TrendingDownIcon,
  TrendingUp as TrendingUpIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { Box, Chip, Alert as MuiAlert, Typography } from '@mui/material'
import React from 'react'
import { useStore, useTranslate } from 'react-admin'
import { formatGlucoseForDisplay } from '../common'
import { getAlertSeverityColor } from '../types/alerts'
import { type GlucoseUnit } from '../utils/glucoseUnits'
import { glucoseDirection } from './alertGlucoseDirection'
import { AlertContext, useAlertContext } from './useAlertContext'

const getAlertIcon = (alertType: string, severity: string) => {
  const color = getAlertSeverityColor(severity)
  const iconProps = { style: { color, fontSize: 16 } }

  switch (glucoseDirection(alertType)) {
    case 'down':
      return <TrendingDownIcon {...iconProps} />
    case 'up':
      return <TrendingUpIcon {...iconProps} />
    default:
      return severity === 'critical' ? (
        <ErrorIcon {...iconProps} />
      ) : severity === 'high' ? (
        <WarningIcon {...iconProps} />
      ) : (
        <InfoIcon {...iconProps} />
      )
  }
}

export interface AlertContextBannerProps {
  // If provided, use this context instead of fetching from hook
  alertContext?: AlertContext | null
  // Callback for clearing the context
  onClear?: () => void
  // Custom styling
  sx?: any
}

export const AlertContextBanner: React.FC<AlertContextBannerProps> = ({
  alertContext: propAlertContext,
  onClear: propOnClear,
  sx,
}) => {
  const translate = useTranslate()
  const { alertContext: hookAlertContext, clearAlertContext } = useAlertContext()
  const [glucoseUnit] = useStore<GlucoseUnit>('glucoseUnit', 'mmol/L')

  // Use prop context if provided, otherwise use hook
  const alertContext = propAlertContext !== undefined ? propAlertContext : hookAlertContext
  const onClear = propOnClear || clearAlertContext

  if (!alertContext) {
    return null
  }

  return (
    <MuiAlert
      severity={
        alertContext.severity === 'critical'
          ? 'error'
          : alertContext.severity === 'high'
            ? 'warning'
            : 'info'
      }
      sx={{ mb: 2, ...sx }}
      icon={getAlertIcon(alertContext.alertType, alertContext.severity)}
      action={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {alertContext.glucoseValue && (
            <Chip
              // alert_events.glucose_value is stored already BGM-corrected, in
              // mmol/L (verified against prod 2026-08-02: the alert value equals
              // 1.46286*raw^0.7971 exactly). So it needs no correction here —
              // but it DOES need unit conversion AND the out-of-range mask:
              // the Go engine clamps a sensor drop-out to exactly 2.2 mmol/L,
              // which every chart masks as "<2.2" / "<40" and the bare
              // formatGlucoseWithUnit printed as a confident "2.2 mmol/L".
              label={formatGlucoseForDisplay(alertContext.glucoseValue, glucoseUnit)}
              size="small"
              color="primary"
            />
          )}
          <Chip
            label={new Date(alertContext.timestamp).toLocaleString()}
            size="small"
            variant="outlined"
          />
          <Typography
            variant="caption"
            sx={{ cursor: 'pointer', textDecoration: 'underline' }}
            onClick={onClear}
          >
            {translate('pos.common.clear')}
          </Typography>
        </Box>
      }
    >
      <Typography variant="body2">
        <strong>{translate('pos.alerts.alert_context')}:</strong>{' '}
        {alertContext.title || translate('pos.alerts.fallback_title')}
      </Typography>
    </MuiAlert>
  )
}

export default AlertContextBanner
